// One-click export of any table as CSV (opens as a file in the browser; the artifact preview cannot download, and says so).
import type { XSheet } from './xlsx';

/** A text cell that a spreadsheet would read as a formula (= + - @, or a leading tab / carriage return) is written as
 *  literal text, prefixed with ' — so a record value can never run in Excel or Sheets. Real numbers stay numbers, and so
 *  does a percentage written by the export itself ("-12.5%"). */
const SAFE_NUMBER = /^[+-]?\d+(\.\d+)?%$/;
export const csvCell = (v: unknown): string => {
  let s = v == null ? '' : String(v);
  if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s) && !SAFE_NUMBER.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const toCsv = (columns: string[], rows: (string | number | null | undefined)[][]): string =>
  [columns.map(csvCell).join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');

/** A sheet as CSV, line for line what the Excel export writes: the title, the filter / as-of / source lines, a blank line,
 *  the headers, the rows, the totals. Percentages are written as Excel shows them ("12.5%", a change "+12.5%"), so a CSV
 *  opened in Excel reads the same value; money to the paisa; dates as yyyy-mm-dd; missing stays blank, never 0. */
export function sheetCsv(s: XSheet): string {
  const val = (v: unknown, kind: string): string | number | null => {
    if (v == null || v === '' || (typeof v === 'number' && !Number.isFinite(v))) return null;
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v !== 'number') return String(v);
    if (kind === 'pct') return `${(v * 100).toFixed(1)}%`;
    if (kind === 'delta') return `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
    return Math.round(v * 100) / 100;
  };
  const head = [s.title, ...(s.notes ?? [])].filter((x): x is string => !!x).map(x => csvCell(x));
  const body = toCsv(s.columns.map(c => c.title), [...s.rows, ...(s.totals ? [s.totals] : [])].map(r => s.columns.map((c, i) => val(r[i], c.kind))));
  return [...head, ...(head.length ? [''] : []), body].join('\n');
}

export function downloadText(fileName: string, text: string, type = 'text/csv;charset=utf-8') {
  try { const blob = new Blob(['﻿' + text], { type }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); } catch { /* blocked: nothing to do */ }
}
export function downloadCsv(name: string, columns: string[], rows: (string | number | null | undefined)[][]) {
  downloadText(`${name}.csv`, toCsv(columns, rows));
}
